import { TransactionJsonModel } from '../domain/stream/transaction/TransactionDomains';
import moment from 'moment/moment';
import { Group, Role, UserSystemAttributes } from '../domain/rbac/RbacDomains';
import { CaseReason, EscalatedReason, RuleJsonModel } from '../domain/collection/rule/FlowDomains';
import { Syntax } from '@trlogic/ui-pack';
import {
  ActionToken,
  ConditionToken,
  EventToken,
  FraudToken,
  NotFraudToken,
  SuspiciousToken,
  WorkflowToken,
  RuleTypes,
} from '../domain/collection/rule/RuleDomains';
import { generateUUID, generateUUIDV4 } from './Generator';
import VariableType from '../constant/VariableType';
import DataType from '../constant/DataType';
import { DataStructure } from '../domain/structure/DataStructureDomains';
import { CommonConnectionModel } from '../domain/connection/ConnectionDomains';
import { CaseSettings } from '../domain/case/CaseDomains';
import { ApiVariable, BasicVariable, ListVariable, QueryVariable, VelocityVariable } from '../domain/enrichment/EnrichmentDomain';
import HttpMethod from '../constant/HttpMethod';
import { List } from '../domain/list/ListDomains';
import { VariableProperties } from '../domain/datatype/VariablePropertiesDomains';
import { ResponseFormats } from '../constant/ResponseFormats';
import { StreamJsonModel } from '../domain/stream/stream/StreamDomain';
import { ActionJsonModel } from '../domain/collection/rule/action/ActionDomains';
import { FormicaSelectBoxItem } from '../components/custom/FormicaSearchSelectBox';
import { FormicaTimePickerDate } from '../components/custom/custom-time-picker/TimePickerDomain';
import { CustomFilter } from '../domain/ui/FilterDomains';
import Tracker, { EventTrackerPlatform } from '../domain/tracker/Tracker';
import { MLOpsDatasourceHistorical, MLOpsModel } from '../domain/mlops/MLOpsDomains';
import { query } from '@trlogic/client-js';
import { FieldType, SuspiciousActivityReport } from '../domain/case/SuspiciousActivityReport';
import { CraSegmentType, CraVariableSchema, PolicyJsonModel } from '../domain/cra/CraDomains';
import { CraType } from '../constant/Constants';
import { CraApiVariableSchema } from '../domain/cra/CraDomains';

class ValueFactory {
  public static initializeEvent(): TransactionJsonModel {
    return {
      name: '',
      variables: [],
      status: false,
      active: 'Active',
      modified: false,
      version: 'v0',
      description: '',
      permission: 'Edit',
      collaborators: [],
      stage: 'DRAFT',
      createdAt: moment().valueOf(),
      updatedAt: moment().valueOf(),
    };
  }

  public static initializeDomainConfiguration(): CaseSettings {
    return {
      name: '',
      dataSource: '',
      panels: [],
      graphConfiguration: [],
      closeCodes: [],
      blacklists: [],
      collaborators: [],
      description: '',
      keyField: '',
      permission: 'Edit',
      transactionAmountField: undefined,
      transactionCurrencyField: undefined,
      suspiciousActivityReport: this.initializeSuspiciousActivityReport(),
      assignmentCandidates: [],
      assignmentCandidatesRequired: false,
      graphDefaultFilterRequired: false,
      graphDefaultFilter: { startDate: undefined, endDate: undefined, field: undefined },
      goOldestCase: false,
      allowAssign: false,
      allowAssignUsers: null,
      bulkCloseRestrictedUsers: [],
      actions: [],
      actionRequired: false,
      reasonRequired: false,
      sla: {
        timeToDone: {
          type: 'minute',
          value: undefined,
        },
        timeToFirstAssign: {
          type: 'minute',
          value: undefined,
        },
      },
      additionalChannels: [],
      entities: [],
      createdAt: moment().valueOf(),
      updatedAt: moment().valueOf(),
    };
  }

  public static initializeSuspiciousActivityReport(): SuspiciousActivityReport {
    return {
      yukumlu: {
        label: 'Yükümlü',
        value: '',
        type: FieldType.ZORUNLU,
      },
      temelBilgi: {
        label: 'Temel Bilgi',
        value: {
          bildirimKategorisi: {
            label: 'Bildirim Kategorisi',
            value: '',
            type: FieldType.ZORUNLU,
          },
          yerelReferansNo: {
            label: 'Yerel Referans No',
            value: '',
            type: FieldType.ZORUNLU,
          },
          oncekiBildirimReferansNo: {
            label: 'Önceki Bildirim Referans No',
            value: '',
            type: FieldType.OPSIYONEL,
          },
          aciliyet: {
            label: 'Aciliyet',
            value: '',
            type: FieldType.ZORUNLU,
          },
          terorOrgutu: {
            label: 'Terör Örgütü',
            value: '',
            type: FieldType.KOSULLU_ZORUNLU,
          },
        },
        type: FieldType.ZORUNLU,
      },
      supheliIslem: {
        label: 'Şüpheli İşlem',
        value: {
          islemCesidi: {
            label: 'İşlem Çeşidi',
            value: '',
            type: FieldType.ZORUNLU,
          },
          islemTarihi1: {
            label: 'İşlem Tarihi 1',
            value: '',
            type: FieldType.ZORUNLU,
          },
          islemTarihi2: {
            label: 'İşlem Tarihi 2',
            value: '',
            type: FieldType.KOSULLU_ZORUNLU,
          },
          islemDurumu: {
            label: 'İşlem Durumu',
            value: '',
            type: FieldType.ZORUNLU,
          },
          hesabaBagli: {
            label: 'Hesaba Bağlı',
            value: '',
            type: FieldType.ZORUNLU,
          },
          tutarTP: {
            label: 'Tutar TP',
            value: '',
            type: FieldType.KOSULLU_ZORUNLU,
          },
          tutar: {
            label: 'Tutar',
            value: '',
            type: FieldType.KOSULLU_ZORUNLU,
          },
          paraBirimi: {
            label: 'Para Birimi',
            value: '',
            type: FieldType.KOSULLU_ZORUNLU,
          },
          islemTuru: {
            label: 'İşlem Türü',
            value: '',
            type: FieldType.KOSULLU_ZORUNLU,
          },
          islemKanali: {
            label: 'İşlem Kanalı',
            value: '',
            type: FieldType.KOSULLU_ZORUNLU,
          },
          ipv4: {
            label: 'IPV4',
            value: '',
            type: FieldType.OPSIYONEL,
          },
          port: {
            label: 'Port',
            value: '',
            type: FieldType.OPSIYONEL,
          },
          islemAciklamasi: {
            label: 'İşlem Açıklaması',
            value: '',
            type: FieldType.OPSIYONEL,
          },
          ulkeKodu: {
            label: 'Ülke Kodu',
            value: '',
            type: FieldType.KOSULLU_ZORUNLU,
          },
          ilKodu: {
            label: 'İl Kodu',
            value: '',
            type: FieldType.KOSULLU_ZORUNLU,
          },
          ilceKodu: {
            label: 'İlçe Kodu',
            value: '',
            type: FieldType.KOSULLU_ZORUNLU,
          },
          isleminGerceklestigiYer: {
            label: 'İşlemin Gerçekleştiği Yer',
            value: '',
            type: FieldType.KOSULLU_ZORUNLU,
          },
          isleminTetiklendigiYer: {
            label: 'İşlemin Tetiklendiği Yer',
            value: '',
            type: FieldType.ZORUNLU,
          },
          islemeAracilikEtmeDurumu: {
            label: 'İşleme Aracılık Etme Durumu',
            value: '',
            type: FieldType.ZORUNLU,
          },
          islemeAracilikEdenKurum: {
            label: 'İşleme Aracılık Eden Kurum',
            value: '',
            type: FieldType.KOSULLU_ZORUNLU,
          },
        },
        type: FieldType.ZORUNLU,
      },
      hesap: {
        label: 'Hesap',
        value: {
          sahisFirmasiHesabi: {
            label: 'Şahıs Firması Hesabı',
            value: '',
            type: FieldType.ZORUNLU,
          },
          hesapTuru: {
            label: 'Hesap Türü',
            value: '',
            type: FieldType.ZORUNLU,
          },
          hesapNo: {
            label: 'Hesap No',
            value: '',
            type: FieldType.OPSIYONEL,
          },
          iban: {
            label: 'IBAN',
            value: '',
            type: FieldType.OPSIYONEL,
          },
          acilisTarihi: {
            label: 'Açılış Tarihi',
            value: '',
            type: FieldType.ZORUNLU,
          },
          kapanisTarihi: {
            label: 'Kapanış Tarihi',
            value: '',
            type: FieldType.KOSULLU_ZORUNLU,
          },
          sonIslemTarihi: {
            label: 'Son İşlem Tarihi',
            value: '',
            type: FieldType.OPSIYONEL,
          },
          bakiyeTP: {
            label: 'Bakiye TP',
            value: '',
            type: FieldType.KOSULLU_ZORUNLU,
          },
          bakiye: {
            label: 'Bakiye',
            value: '',
            type: FieldType.KOSULLU_ZORUNLU,
          },
          paraBirimi: {
            label: 'Para Birimi',
            value: '',
            type: FieldType.ZORUNLU,
          },
          kisitlamaTuru: {
            label: 'Kısıtlama Türü',
            value: '',
            type: FieldType.OPSIYONEL,
          },
          kisitlamaDigerAciklama: {
            label: 'Kısıtlama Diğer Açıklama',
            value: '',
            type: FieldType.KOSULLU_ZORUNLU,
          },
          hesapSahibi: {
            label: 'Hesap Sahibi',
            value: {
              magdur: {
                label: 'Mağdur',
                value: '',
                type: FieldType.ZORUNLU,
              },
              adSoyadUnvan: {
                label: 'Ad/Soyad/Unvan',
                value: '',
                type: FieldType.ZORUNLU,
              },
              tckn: {
                label: 'TCKN',
                value: '',
                type: FieldType.OPSIYONEL,
              },
              vkn: {
                label: 'VKN',
                value: '',
                type: FieldType.OPSIYONEL,
              },
              ykn: {
                label: 'YKN',
                value: '',
                type: FieldType.OPSIYONEL,
              },
              passaport: {
                label: 'Pasaport',
                value: '',
                type: FieldType.OPSIYONEL,
              },
            },
            type: FieldType.ZORUNLU,
          },
        },
        type: FieldType.OPSIYONEL,
      },
      supheKategorisi: {
        label: 'Şüphe Kategorisi',
        value: '',
        type: FieldType.ZORUNLU,
      },
      bildirimAciklama: {
        label: 'Bildirim Açıklama',
        value: '',
        type: FieldType.ZORUNLU,
      },
      gercekKisi: {
        label: 'Gerçek Kişi',
        value: {
          islemleBaglantisi: {
            label: 'İşlemle Bağlantısı',
            value: '',
            type: FieldType.KOSULLU_ZORUNLU,
          },
          islemleBaglantisiAciklama: {
            label: 'İşlemle Bağlantısı Açıklama',
            value: '',
            type: FieldType.KOSULLU_ZORUNLU,
          },
          adi: {
            label: 'Adı',
            value: '',
            type: FieldType.ZORUNLU,
          },
          soyadi: {
            label: 'Soyadı',
            value: '',
            type: FieldType.ZORUNLU,
          },
          tckn: {
            label: 'TCKN',
            value: '',
            type: FieldType.KOSULLU_ZORUNLU,
          },
          vkn: {
            label: 'VKN',
            value: '',
            type: FieldType.OPSIYONEL,
          },
          ykn: {
            label: 'YKN',
            value: '',
            type: FieldType.KOSULLU_ZORUNLU,
          },
          dogumTarihi: {
            label: 'Doğum Tarihi',
            value: '',
            type: FieldType.KOSULLU_ZORUNLU,
          },
          dogumYeri: {
            label: 'Doğum Yeri',
            value: '',
            type: FieldType.OPSIYONEL,
          },
          anaAdi: {
            label: 'Ana Adı',
            value: '',
            type: FieldType.OPSIYONEL,
          },
          babaAdi: {
            label: 'Baba Adı',
            value: '',
            type: FieldType.OPSIYONEL,
          },
          uyrugu: {
            label: 'Uyruğu',
            value: '',
            type: FieldType.KOSULLU_ZORUNLU,
          },
          cinsiyeti: {
            label: 'Cinsiyeti',
            value: '',
            type: FieldType.KOSULLU_ZORUNLU,
          },
          kimlikBelgesi: {
            label: 'Kimlik Belgesi',
            value: {
              belgeTuru: {
                label: 'Belge Türü',
                value: '',
                type: FieldType.ZORUNLU,
              },
              belgeNumarasi: {
                label: 'Belge Numarası',
                value: '',
                type: FieldType.ZORUNLU,
              },
              gecerlilikTarihi: {
                label: 'Geçerlilik Tarihi',
                value: '',
                type: FieldType.OPSIYONEL,
              },
            },
            type: FieldType.KOSULLU_ZORUNLU,
          },
          meslek: {
            label: 'Meslek',
            value: '',
            type: FieldType.KOSULLU_ZORUNLU,
          },
          ekBilgi: {
            label: 'Ek Bilgi',
            value: '',
            type: FieldType.OPSIYONEL,
          },
          adres: {
            label: 'Adres',
            value: {
              adresSatir: {
                label: 'Adres Satır',
                value: '',
                type: FieldType.ZORUNLU,
              },
              postaKodu: {
                label: 'Posta Kodu',
                value: '',
                type: FieldType.OPSIYONEL,
              },
              ilKodu: {
                label: 'İl Kodu',
                value: '',
                type: FieldType.OPSIYONEL,
              },
              ilceKodu: {
                label: 'İlçe Kodu',
                value: '',
                type: FieldType.OPSIYONEL,
              },
              ulkeKodu: {
                label: 'Ülke Kodu',
                value: '',
                type: FieldType.ZORUNLU,
              },
            },
            type: FieldType.OPSIYONEL,
          },
          iletisimBilgisi: {
            label: 'İletişim Bilgisi',
            value: '',
            type: FieldType.KOSULLU_ZORUNLU,
          },
          kimlikBilgileriSahte: {
            label: 'Kimlik Bilgileri Sahte',
            value: '',
            type: FieldType.ZORUNLU,
          },
          magdur: {
            label: 'Mağdur',
            value: '',
            type: FieldType.ZORUNLU,
          },
          kimlikTespitTuruTip: {
            label: 'Kimlik Tespit Türü Tip',
            value: '',
            type: FieldType.ZORUNLU,
          },
        },
        type: FieldType.KOSULLU_ZORUNLU,
      },
      digerKisi: {
        label: 'Diğer Kişi',
        value: {
          islemleBaglantisi: {
            label: 'İşlemle Bağlantısı',
            value: '',
            type: FieldType.KOSULLU_ZORUNLU,
          },
          islemleBaglantisiAciklama: {
            label: 'İşlemle Bağlantısı Açıklama',
            value: '',
            type: FieldType.KOSULLU_ZORUNLU,
          },
          kurulusNevi: {
            label: 'Kuruluş Nevi',
            value: '',
            type: FieldType.ZORUNLU,
          },
          unvan: {
            label: 'Ünvan',
            value: '',
            type: FieldType.ZORUNLU,
          },
          isletmeAdi: {
            label: 'İşletme Adı',
            value: '',
            type: FieldType.OPSIYONEL,
          },
          vkn: {
            label: 'VKN',
            value: '',
            type: FieldType.OPSIYONEL,
          },
          sicilNo: {
            label: 'Sicil No',
            value: '',
            type: FieldType.OPSIYONEL,
          },
          faaliyetKonusu: {
            label: 'Faaliyet Konusu',
            value: '',
            type: FieldType.OPSIYONEL,
          },
          ulkeKodu: {
            label: 'Ülke Kodu',
            value: '',
            type: FieldType.KOSULLU_ZORUNLU,
          },
          ilKodu: {
            label: 'İl Kodu',
            value: '',
            type: FieldType.OPSIYONEL,
          },
          ilceKodu: {
            label: 'İlçe Kodu',
            value: '',
            type: FieldType.OPSIYONEL,
          },
          kurulusTarihi: {
            label: 'Kuruluş Tarihi',
            value: '',
            type: FieldType.OPSIYONEL,
          },
          websitesi: {
            label: 'Websitesi',
            value: '',
            type: FieldType.OPSIYONEL,
          },
          ekBilgi: {
            label: 'Ek Bilgi',
            value: '',
            type: FieldType.OPSIYONEL,
          },
          adres: {
            label: 'Adres',
            value: {
              adresSatir: {
                label: 'Adres Satır',
                value: '',
                type: FieldType.ZORUNLU,
              },
              postaKodu: {
                label: 'Posta Kodu',
                value: '',
                type: FieldType.OPSIYONEL,
              },
              ilKodu: {
                label: 'İl Kodu',
                value: '',
                type: FieldType.OPSIYONEL,
              },
              ilceKodu: {
                label: 'İlçe Kodu',
                value: '',
                type: FieldType.OPSIYONEL,
              },
              ulkeKodu: {
                label: 'Ülke Kodu',
                value: '',
                type: FieldType.ZORUNLU,
              },
            },
            type: FieldType.OPSIYONEL,
          },
          iletisimBilgisi: {
            label: 'İletişim Bilgisi',
            value: '',
            type: FieldType.KOSULLU_ZORUNLU,
          },
          kimlikBilgileriSahte: {
            label: 'Kimlik Bilgileri Sahte',
            value: '',
            type: FieldType.ZORUNLU,
          },
          magdur: {
            label: 'Mağdur',
            value: '',
            type: FieldType.ZORUNLU,
          },
        },
        type: FieldType.KOSULLU_ZORUNLU,
      },
    };
  }

  public static initializeRule(): RuleJsonModel {
    return {
      name: '',
      createdDate: moment().valueOf(),
      priority: 1,
      score: 50,
      description: '',
      status: 'DRAFT',
      rule: this.initializeExpression(),
      resolutions: new Array<any>(),
      permission: 'Edit',
      collaborators: [],
      riskType: 'fraud',
      type: RuleTypes.MONITORING,
      workflow: {
        id: 'start_start',
        value: {
          name: 'Start',
          color: 'green',
          type: 'Start',
          icon: 'start',
          status: true,
        },
        children: [
          {
            id: 'event_event',
            value: {
              type: 'Event',
              name: '',
              color: 'black',
              icon: 'event',
              status: false,
            } as WorkflowToken,
            children: [
              {
                id: 'priority_priority',
                value: {
                  name: 'Priority',
                  type: 'Priority' as any,
                  color: 'orange',
                  icon: 'priority',
                  status: false,
                } as WorkflowToken,
                children: [
                  {
                    id: 'condition_condition',
                    value: {
                      name: 'Condition',
                      type: 'Condition',
                      color: 'orange',
                      icon: 'condition',
                      status: false,
                    } as WorkflowToken,
                    children: [
                      {
                        id: 'caseTrue',
                        value: {
                          name: 'Resolution',
                          reason: CaseReason.SUSPICIOUS,
                          score: 50,
                          icon: 'notFraud',
                          type: 'NotFraud',
                          status: false,
                        } as any,
                        children: [
                          {
                            id: 'action_action0',
                            value: {
                              type: 'Action',
                              name: 'Action0',
                              color: '#ADD8E6',
                              icon: 'action',
                              payload: {
                                template: 'Action0',
                              },
                              status: false,
                            } as any,
                            children: [],
                          },
                        ],
                      },
                      /*
                      No condition
                      {
                        id: "caseFalse",
                        value: {
                          name: "Resolution",
                          reason: CaseReason.FRAUD,
                          score: 0,
                          icon: "fraud",
                          type: "Fraud",
                          status: false
                        } as any,
                        children: [
                          {
                            id: "action_action1",
                            value: {
                              type: "Action",
                              name: "Action1",
                              color: "#ADD8E6",
                              icon: "action",
                              payload: {
                                template: "Action1"
                              },
                              status: false
                            } as any,
                            children: []
                          }
                        ]
                      }*/
                    ],
                  },
                ],
              },
            ],
          },
        ],
      } as Syntax<WorkflowToken>,
      stage: 'DRAFT',
      createdAt: moment().valueOf(),
      updatedAt: moment().valueOf(),
    };
  }

  public static initializeResolutionRule(): RuleJsonModel {
    return {
      name: '',
      createdDate: moment().valueOf(),
      priority: 1,
      description: '',
      status: 'DRAFT',
      rule: this.initializeExpression(),
      resolutions: new Array<any>(),
      permission: 'Edit',
      collaborators: [],
      riskType: 'fraud',
      type: RuleTypes.RESOLUTION,
      workflow: {
        id: 'start_start',
        value: {
          name: 'Start',
          color: 'green',
          type: 'Start',
          icon: 'start',
          status: true,
        },
        children: [
          {
            id: 'event_event',
            value: {
              type: 'Event',
              name: '',
              color: 'black',
              icon: 'event',
              status: false,
            } as WorkflowToken,
            children: [
              {
                id: 'priority_priority',
                value: {
                  name: 'Priority',
                  type: 'Priority' as any,
                  color: 'orange',
                  icon: 'priority',
                  status: false,
                } as WorkflowToken,
                children: [
                  {
                    id: 'condition_condition',
                    value: {
                      name: 'Condition',
                      type: 'Condition',
                      color: 'orange',
                      icon: 'condition',
                      status: false,
                    } as WorkflowToken,
                    children: [
                      {
                        id: 'escalated',
                        value: {
                          name: 'Escalated',
                          reason: EscalatedReason.DEFAULT,
                          type: 'Escalated' as any,
                          backgroundColor: '#FFF8E4',
                          status: false,
                        } as any,
                        children: [
                          {
                            id: 'action_action0',
                            value: {
                              type: 'Action',
                              name: 'Action0',
                              color: '#ADD8E6',
                              icon: 'action',
                              payload: {
                                template: 'Action0',
                              },
                              status: false,
                            } as any,
                            children: [],
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      } as Syntax<WorkflowToken>,
      stage: 'DRAFT',
      createdAt: moment().valueOf(),
      updatedAt: moment().valueOf(),
    };
  }

  public static initializeRuleByType(type: RuleTypes): RuleJsonModel {
    switch (type) {
      case RuleTypes.RESOLUTION:
        return this.initializeResolutionRule();
      default:
        return this.initializeRule();
    }
  }

  public static initializeRole(): Role {
    return {
      name: '',
      description: '',
      menu: [
        {
          baseMenu: 'Dashboard',
          permission: 'No Permission',
          subMenu: [],
        },
        {
          baseMenu: 'Cases',
          permission: 'No Permission',
          subMenu: [],
        },
        {
          baseMenu: 'Entity',
          permission: 'No Permission',
          subMenu: [],
        },
        {
          baseMenu: 'Graph',
          permission: 'No Permission',
          subMenu: [],
        },
        {
          baseMenu: 'CaseConfiguration',
          permission: 'No Permission',
          subMenu: [],
        },
        {
          baseMenu: 'Watchlist',
          permission: 'No Permission',
          subMenu: [],
        },
        {
          baseMenu: 'WatchlistAlert',
          permission: 'No Permission',
          subMenu: [],
        },
        {
          baseMenu: 'Transaction',
          permission: 'No Permission',
          subMenu: [],
        },
        {
          baseMenu: 'List',
          permission: 'No Permission',
          subMenu: [
            {
              baseMenu: 'TableList',
              permission: 'No Permission',
              subMenu: [],
            },
            {
              baseMenu: 'QueryList',
              permission: 'No Permission',
              subMenu: [],
            },
            {
              baseMenu: 'CollectionList',
              permission: 'No Permission',
              subMenu: [],
            },
          ],
        },
        {
          baseMenu: 'Stream',
          permission: 'No Permission',
          subMenu: [],
        },
        {
          baseMenu: 'Enrichment',
          permission: 'No Permission',
          subMenu: [
            {
              baseMenu: 'EnrichmentVelocity',
              permission: 'No Permission',
              subMenu: [],
            },
            {
              baseMenu: 'EnrichmentList',
              permission: 'No Permission',
              subMenu: [],
            },
            {
              baseMenu: 'EnrichmentApi',
              permission: 'No Permission',
              subMenu: [],
            },
            {
              baseMenu: 'EnrichmentQuery',
              permission: 'No Permission',
              subMenu: [],
            },
            {
              baseMenu: 'EnrichmentBasic',
              permission: 'No Permission',
              subMenu: [],
            },
          ],
        },
        {
          baseMenu: 'Rule',
          permission: 'No Permission',
          subMenu: [],
        },
        {
          baseMenu: 'Connection',
          permission: 'No Permission',
          subMenu: [],
        },
        {
          baseMenu: 'Struct',
          permission: 'No Permission',
          subMenu: [],
        },
        {
          baseMenu: 'Ai-Model',
          permission: 'No Permission',
          subMenu: [],
        },
        {
          baseMenu: 'Tracker',
          permission: 'No Permission',
          subMenu: [],
        },
        {
          baseMenu: 'ACH',
          permission: 'No Permission',
          subMenu: [],
        },
        {
          baseMenu: 'Action',
          permission: 'No Permission',
          subMenu: [],
        },
        {
          baseMenu: 'Sandbox',
          permission: 'No Permission',
          subMenu: [
            {
              baseMenu: 'SandboxSimulator',
              permission: 'No Permission',
              subMenu: [],
            },
            {
              baseMenu: 'SandboxCsv',
              permission: 'No Permission',
              subMenu: [],
            },
            {
              baseMenu: 'SandboxJson',
              permission: 'No Permission',
              subMenu: [],
            },
          ],
        },
        {
          baseMenu: 'Cra',
          permission: 'No Permission',
          subMenu: [],
        },
      ],
      enabled: true,
      createdDate: moment().valueOf(),
      lastUpdated: moment().valueOf(),
    };
  }

  public static initializeGroup(): Group {
    return {
      id: '',
      name: '',
      description: '',
      role: 'Member',
      roles: [],
      permission: 0,
      users: [],
      enabled: true,
      createdAt: moment().valueOf(),
      updatedAt: moment().valueOf(),
    };
  }

  public static initializeExpression(): Syntax {
    return {
      id: generateUUID(),
      children: [],
      value: {
        type: 'TokenGroup',
      },
    };
  }

  public static initializeWorkflowEvent(): Syntax<EventToken> {
    return {
      id: `event_${generateUUID()}`,
      value: {
        type: 'Event',
        name: '',
        color: 'black',
        icon: 'event',
        status: false,
      },
      children: [],
    };
  }

  public static initializeWorkflowConditionToken(): Syntax<ConditionToken> {
    return {
      id: `condition_${generateUUID()}`,
      value: {
        name: '',
        type: 'Condition',
        color: 'orange',
        icon: 'condition',
      } as ConditionToken,
      children: [],
    };
  }

  public static initializeWorkFlowFraudToken(): Syntax<FraudToken> {
    return {
      id: `resolution_fraud_${generateUUID()}`,
      value: {
        name: '',
        reason: CaseReason.FRAUD,
        icon: 'fraud',
        type: 'Fraud',
        color: 'red',
      } as FraudToken,
      children: [],
    };
  }

  public static initializeWorkFlowNotFraudToken(): Syntax<NotFraudToken> {
    return {
      id: `resolution_not_fraud_${generateUUID()}`,
      value: {
        name: '',
        reason: CaseReason.NOT_FRAUD,
        icon: 'notFraud',
        type: 'NotFraud',
      } as NotFraudToken,
      children: [],
    };
  }

  public static initializeWorkFlowSuspiciousToken(): Syntax<SuspiciousToken> {
    return {
      id: `resolution_suspicious_${generateUUID()}`,
      value: {
        name: '',
        reason: CaseReason.SUSPICIOUS,
        icon: 'suspicious',
        type: 'Suspicious',
      } as SuspiciousToken,
      children: [],
    };
  }

  public static initializeWorkflowActionToken(): Syntax<ActionToken> {
    return {
      id: `action_${generateUUID()}`,
      value: {
        type: 'Action',
        name: '',
        color: '#ADD8E6',
        icon: 'action',
        payload: {
          template: '',
        },
        status: false,
      },
      children: [],
    };
  }

  public static initializeList(): List {
    return {
      name: '',
      description: '',
      fields: [],
      type: 'INTERNAL_LIST',
      collaborators: [],
      permission: 'Edit',
      stage: 'DRAFT',
      createdAt: moment().valueOf(),
      updatedAt: moment().valueOf(),
    };
  }

  public static initializeConnection(): CommonConnectionModel {
    return {
      name: '',
      description: '',
      databaseType: 'Oracle Database',
      databaseName: '',
      dataCenter: '',
      hostname: '',
      port: 0,
      username: '',
      password: '',
      status: false,
      connectionString: '',
      permission: 'Edit',
      collaborators: [],
      stage: 'DRAFT',
      createdAt: moment().valueOf(),
      updatedAt: moment().valueOf(),
    } as CommonConnectionModel;
  }

  public static initializeAction(): ActionJsonModel {
    return {
      name: '',
      description: '',
      template: '',
      type: 'transaction',
      transaction: '',
      timeout: 100,
      permission: 'Edit',
      collaborators: [],
      stage: 'DRAFT',
      createdAt: moment().valueOf(),
      updatedAt: moment().valueOf(),
    };
  }

  public static initializeBasicVariable(): BasicVariable {
    return {
      name: '',
      value: '',
      properties: { dataType: DataType.STRING },
      description: '',
      status: false,
      permission: 'Edit',
      collaborators: [],
      type: VariableType.BASIC,
      stage: 'DRAFT',
      createdAt: moment().valueOf(),
      updatedAt: moment().valueOf(),
    };
  }

  public static initializeQueryVariable(): QueryVariable {
    return {
      name: '',
      properties: { dataType: DataType.STRING },
      description: '',
      status: false,
      connection: '',
      transaction: '',
      query: '',
      permission: 'Edit',
      collaborators: [],
      type: VariableType.QUERY,
      timeout: 1,
      stage: 'DRAFT',
      createdAt: moment().valueOf(),
      updatedAt: moment().valueOf(),
    };
  }

  public static initializeApiVariable(): ApiVariable {
    return {
      name: '',
      description: '',
      url: '',
      method: HttpMethod.GET,
      headers: {},
      body: '',
      parameters: [],
      responseType: ResponseFormats.JSON,
      properties: { dataType: DataType.STRING },
      status: false,
      transaction: '',
      type: VariableType.API,
      permission: 'Edit',
      collaborators: [],
      timeout: 100,
      stage: 'DRAFT',
      createdAt: moment().valueOf(),
      updatedAt: moment().valueOf(),
    };
  }

  public static initializeListVariable(): ListVariable {
    return {
      name: '',
      description: '',
      list: '',
      transaction: '',
      field: 'field',
      key: {
        id: generateUUIDV4(),
        value: { type: 'TokenGroup' },
        children: [],
      },
      properties: { dataType: DataType.STRING },
      status: false,
      permission: 'Edit',
      collaborators: [],
      type: VariableType.LIST,
      stage: 'DRAFT',
      createdAt: moment().valueOf(),
      updatedAt: moment().valueOf(),
    };
  }

  public static initializeStream(): StreamJsonModel {
    return {
      name: '',
      description: '',
      columns: [],
      transaction: '',
      key: {
        id: generateUUIDV4(),
        value: { type: 'TokenGroup' },
        children: [],
      },
      rowTime: '',
      where: { id: 'where', value: { type: 'TokenGroup' }, children: [] },
      version: 'v0',
      permission: 'Edit',
      collaborators: [],
      stage: 'DRAFT',
      createdAt: moment().valueOf(),
      updatedAt: moment().valueOf(),
    };
  }

  public static initializeTracker(): Tracker {
    return {
      name: '',
      description: '',
      platform: EventTrackerPlatform.MOBILE,
      triggers: [],
      variables: [],
      event: {
        name: '',
        keyMapping: '',
        variableMappings: {},
      },
      stage: 'DRAFT',
      permission: 'Edit',
      collaborators: [],
      createdAt: moment().valueOf(),
      updatedAt: moment().valueOf(),
    };
  }

  public static initializePolicy(assessor?: CraType): PolicyJsonModel {
    return {
      id: 'Individual',
      name: 'Customer Risk Assessment',
      description: '',
      stage: 'DRAFT',
      assessor: assessor || 'Individual',
      permission: 'Edit',
      collaborators: [],
      version: '',
      status: false,
      variables: [],
      createdAt: moment().valueOf(),
      updatedAt: moment().valueOf(),
    };
  }

  public static initializeCraVariableSchema(type?: CraSegmentType): CraVariableSchema {
    return {
      name: '',
      type: type || CraSegmentType.STRING,
      isEnabled: true,
      weight: 1,
      description: '',
    };
  }

  public static initializeVelocityVariable(): VelocityVariable {
    return {
      name: '',
      description: '',
      properties: { dataType: DataType.NUMBER },
      status: false,
      permission: 'Edit',
      collaborators: [],
      transaction: '',
      stream: '',
      skip: 0,
      limit: 0,
      function: 'sum',
      select: { id: 'select', value: { type: 'TokenGroup' }, children: [] },
      groupBy: { id: 'groupby', value: { type: 'TokenGroup' }, children: [] },
      where: { id: 'where', value: { type: 'TokenGroup' }, children: [] },
      tenant: '',
      startDate: {} as FormicaTimePickerDate,
      endDate: {} as FormicaTimePickerDate,
      type: VariableType.VELOCITY,
      version: 'v0',
      stage: 'DRAFT',
      createdAt: moment().valueOf(),
      updatedAt: moment().valueOf(),
    };
  }

  public static initializeDataStructure(): DataStructure {
    return {
      name: '',
      description: '',
      properties: {
        variables: [
          {
            name: '',
            description: '',
            properties: { dataType: DataType.STRING },
            variable: undefined,
          },
        ],
        dataType: DataType.OBJECT,
      },
      createdDate: moment().valueOf(),
      lastUpdatedDate: moment().valueOf(),
      permission: 'Edit',
      collaborators: [],
      createdAt: moment().valueOf(),
      updatedAt: moment().valueOf(),
    };
  }

  public static initializeAiModel(): MLOpsModel {
    return {
      name: '',
      description: '',
      channel: '',
      version: 'v0',
      trainingDatasource: {
        type: 'HISTORICAL',
        query: ValueFactory.initializeFilter(),
      } as MLOpsDatasourceHistorical,
      validationDatasource: {
        type: 'HISTORICAL',
        query: ValueFactory.initializeFilter(),
      } as MLOpsDatasourceHistorical,
      baseFeatures: [],
      customFeatures: [],
      samplingRatio: 20,
      algorithms: [],
      parameters: {
        maxModels: 10,
        maxRuntimeInMinutes: 60,
        sortMetric: 'AUC',
        stoppingMetric: 'AUTO',
      },
      permission: 'Edit',
      collaborators: [],
      stage: 'DRAFT',
      createdAt: moment().valueOf(),
      updatedAt: moment().valueOf(),
    };
  }

  public static getDataTypeOptions(): FormicaSelectBoxItem[] {
    return [
      { label: 'String', value: DataType.STRING, properties: { dataType: DataType.STRING } },
      { label: 'Number', value: DataType.NUMBER, properties: { dataType: DataType.NUMBER } },
      { label: 'Boolean', value: DataType.BOOLEAN, properties: { dataType: DataType.BOOLEAN } },
      { label: 'Date', value: DataType.DATE, properties: { dataType: DataType.DATE } },
    ];
  }

  public static getDataTypeOptionsV2(): CustomSelectOptionsV2[] {
    return [
      { label: 'String', value: { dataType: DataType.STRING } },
      { label: 'Number', value: { dataType: DataType.NUMBER } },
      { label: 'Boolean', value: { dataType: DataType.BOOLEAN } },
      { label: 'Date', value: { dataType: DataType.DATE } },
    ];
  }

  public static encodeBase64(value: string): string {
    const encodedValue = btoa(encodeURIComponent(value));
    let pattern = /=/g;
    return encodedValue.replace(pattern, '');
  }

  public static decodeBase64(value: string): string {
    return decodeURIComponent(atob(value));
  }

  public static initializeFilter(): CustomFilter {
    return {
      type: 'ALL_MATCH',
      fields: [],
    };
  }

  public static initializeUserAttribute(): UserSystemAttributes {
    return {
      id: '',
      username: '',
      filters: [],
      table: [],
    };
  }

  public static initializeCraApiVariableSchema(): CraApiVariableSchema {
    return {
      url: '',
      method: 'GET',
      parameters: [],
      headers: {},
      body: '',
      auth: 'No Auth',
      mapping: '',
      responseType: ResponseFormats.JSON,
      timeout: 100,
      properties: { dataType: DataType.STRING },
    };
  }
}

export interface CustomSelectOptions {
  label: string;
  value: string;
}

export interface CustomSelectOptionsV2 {
  label: string;
  value: VariableProperties;
}

export default ValueFactory;
